import { Lock } from "@mui/icons-material";
//import "@mui/lab";
import { Alert, AlertTitle, Box, Button, Link, Typography, useTheme } from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from 'react-router';
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { IApiProblemDetails } from "../../common/models/IApiProblemDetails";
import { IeLynxUser } from "../../common/models/eLynxUser";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { UserStateActionTypeEnum, useUserContext } from "../../contexts/user-context/UserContext";
import { routesByViewID } from "../../routes/routes";
import eLynxAuthService from "../../services/authentication/elynx-auth-service/ELynxAuthService";
import { RestError } from "../../services/rest-service/RestService";
import storageService from "../../services/storage/storage-service/StorageService";
import { RedirectFromLogoutStorageKey } from "../../views/login-view/LoginViewUtils";
import FormInput from "../forms/form-input/FormInput";
import FormPassword from "../forms/form-password/FormPassword";
import Form from "../forms/form/Form";
import FullScreenDialog from "../full-screen-dialog/FullScreenDialog";
import SettingsBar, { SettingsActionEnum } from "../settings-bar/SettingsBar";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import useChangePasswordState, { ChangePasswordStepEnum } from "./ChangePasswordFormState";
import ChangePasswordFormUtils from "./ChangePasswordFormUtils";
import useNavigateTo from "../../hooks/use-navigate-to/useNavigateTo";
import LoginDataService from "../../services/login-data-service/LoginDataService";

export type ChangePasswordFormProps = {
    open: boolean;
    onClose: () => void;
    onLoading?: (isLoading: boolean) => void;
};

export default function ChangePasswordForm({ open, onClose, onLoading }: ChangePasswordFormProps) {


    const form = useForm({
        mode: 'onSubmit',
        shouldFocusError: true,
        defaultValues: {
            email: "",
            currentpassword: "",
            newpassword: "",
            confirmpassword: "",
            continue: null
        }
    });
    const {
        handleSubmit,
        register,
        clearErrors,
        formState: {
            //errors,
            isValid,
            isDirty
        },
        setFocus,
        getValues,
        reset
    } = form;
    const { t } = useTranslation();

    const [userState, dispatchUserState] = useUserContext();
    const { confirm, acknowledge } = useConfirmation();
    const location = useLocation();
    const navigateTo = useNavigateTo();


    const {
        step,
        setStep,
        newUser,
        setNewUser,
        forgotPasswordGuid,
        setForgotPasswordGuid
    } = useChangePasswordState();

    const [, setAppNotificationState] = useAppNotificationContext();

    const theme = useTheme();

    const passwordRequiredLength =  userState.user?.passwordRequiredLength ?? 8;
    const passwordComplexityRegEx = new RegExp("^((?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])|(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[^a-zA-Z0-9])|(?=.*?[A-Z])(?=.*?[0-9])(?=.*?[^a-zA-Z0-9])|(?=.*?[a-z])(?=.*?[0-9])(?=.*?[^a-zA-Z0-9])).{" + passwordRequiredLength + ",}$");    
    const emailRegEx = new RegExp(/^[A-Za-z0-9+_-][A-Za-z0-9.+_-]{0,63}@[A-Za-z0-9.-]{1,61}\.[A-Za-z]{2,}$/);    

    const onContinue = () => {        

        switch (step) {
            case ChangePasswordStepEnum.Step1:
                if (forgotPasswordGuid) {
                    _onEmail();
                } else {
                    _onCurrentPassword();
                }
                
                break;

            case ChangePasswordStepEnum.NewPassword:
                _onChangePassword();
                break;

            default:
                throw Error(`step ${step} not implememented`);
        }
    };

    const _onEmail = async () => {
        
        const values = getValues();
        if (values.email.trim().toLowerCase() === "demo@elynxtech.com") {

            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Warning,
                show: true,
                message: t('changePassword.demoNotAllowed') 
            });
            return;
        }

        if (forgotPasswordGuid) {
            const loginData = await LoginDataService.getLoginInfo(values.email.trim());

            if (!loginData.changePasswordGuid || forgotPasswordGuid !== loginData.changePasswordGuid) {

                setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Warning,
                show: true,
                //message: t('changePassword.tokenExpired') 
                message:  
                            <Typography component='div' variant='body1'>
                                This password link has already been used. If you have already set your password, please go to
                                <Link 
                                    //color="textPrimary" 
                                    color='inherit'
                                    href='https://elynxapp.elynxtech.com'
                                    target="_blank" 
                                    sx={{
                                        fontWeight: 600
                                    }}>
                                    &nbsp;elynxapp.elynxtech.com&nbsp;
                                </Link>
                                 and enter in your login information. If you need to set a new password, please use the Forgot Password link from the login page.
                            </Typography>



                });

                return;
            }
        }

        setStep(ChangePasswordStepEnum.NewPassword);
        setFocus("newpassword");
    }

    const _onCurrentPassword = async () => {
        try {
            if (onLoading) {
                onLoading(true);
            }

            const values = getValues();
            await ChangePasswordFormUtils.validateCurrentPassword(userState?.user as IeLynxUser, values.currentpassword as string);
            setStep(ChangePasswordStepEnum.NewPassword);
            setFocus("newpassword");

        } catch (error) {

            setFocus("currentpassword");

            const restError = error as RestError;

            if (restError.response?.status === 400) {
                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    show: true,
                    message: restError.response?.data?.message,
                    hideContactInfo: true
                });
            } else {
                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    show: true,
                    error: restError
                });
            }
        } finally {
            if (onLoading) {
                onLoading(false);
            }
        }

    };

    const _onChangePassword = async () => {

        try {

            const values = getValues();

            const email = values.email.trim();
            const currentPassword = values.currentpassword.trim();
            const newPassword = values.newpassword.trim();
            const confirmPassword = values.confirmpassword.trim();

            if (onLoading) {
                onLoading(true);
            }

            if (forgotPasswordGuid) {

                await ChangePasswordFormUtils.updatePassword(email,
                    currentPassword.length ? currentPassword : undefined,
                    newPassword,
                    confirmPassword, 
                    forgotPasswordGuid,
                    newUser);

                    reset();
                    
                    eLynxAuthService.logout();

                    storageService.session.set(RedirectFromLogoutStorageKey, true);
               
                    dispatchUserState({
                      type: UserStateActionTypeEnum.ResetUserState,
                    });
                
                    resetState();

                    acknowledge(t('changePassword.changed'));

                    setTimeout(() => {
                        navigateTo(routesByViewID.get(UIViewEnum.Login)?.fullPath as string, {replace: true});
                    }, 500);
                    
            } else {

                const user = { ...userState.user as IeLynxUser };
                await ChangePasswordFormUtils.changePassword(user, 
                    currentPassword,
                    newPassword,
                    confirmPassword);
    
                acknowledge(t('changePassword.changed'));
    
                resetState();
            }

        } catch (error) {

            const restError = error as RestError;

            const problemDetails = (restError.response?.data as unknown) as IApiProblemDetails;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                message: problemDetails?.message,
                hideContactInfo: true,
                error: !problemDetails?.message ? restError : undefined,
                show: true
            });

        } finally {
            if (onLoading) {
                onLoading(false);
            }
        }
    };

    // custom validator for use form hook
    const _validateNewPassword = (value: string): boolean => {
        const currentPassword = getValues("currentpassword");
        return value !== currentPassword && passwordComplexityRegEx.test(value);
    };

    const _validateConfirmPassword = (value: string): boolean => {
        const newPassword = getValues("newpassword");
        return value === newPassword;
    };


    const _validateEmail = (value: string): boolean => {
        return value.trim().length > 0 && emailRegEx.test(value);
    };

    const resetState = () => {
        reset();
        clearErrors();
        setStep(ChangePasswordStepEnum.Step1);
        onClose();
    };

    const getUserConfirmation = async () => {

        return (await confirm({
            dialogTitle: t('cancelConfirmation.title'),
            confirmationText: t('changePassword.cancelChange'),
            yesButtonText: t('cancelConfirmation.yesButtonText'),
            noButtonText: t('cancelConfirmation.noButtonText'),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        }))  === "yes";
    };

    const onCloseForm = async (action: SettingsActionEnum): Promise<void> => {

        if (action === SettingsActionEnum.OK) {
            // trigger hidden submit buttons for userForm hook validation
            handleSubmit(onContinue);

        } else {
            if (isDirty) {

                const result = await getUserConfirmation();

                if (result) {

                    // ok to cancel changes.
                    resetState();
                }

            } else {
                resetState();
            }
        }
    };

    const canContinue = (step === ChangePasswordStepEnum.Step1 && isDirty && getValues(forgotPasswordGuid ? 'email' : 'currentpassword')?.length > 0) || isValid;

    useEffect(() => {
        if (location.search.length > 0) {

            const params = new URLSearchParams(location.search);
            if (params.has("token")) {
                setForgotPasswordGuid(params.get("token") as string);
                storageService.local.clear();
                storageService.session.clear();
            }

            if (params.has("newUser")) {
                setNewUser((params.get("newUser") as string).toLowerCase() === 'true');
            }

        }

    }, [
        location.search,
        setForgotPasswordGuid,
        setNewUser
    ]);

    return (
        <>
            <FullScreenDialog open={open} onClose={onClose}>
                <SettingsBar 
                    title={forgotPasswordGuid ? '' : t('changePassword.title')} 
                    onClose={onCloseForm} 
                    modified={isDirty} 
                    hideAction={true} 
                    showLogo={forgotPasswordGuid ? true : false}
                    hideClose={forgotPasswordGuid ? true : false}
                    />

                <Form form={form} 
                        onSubmit={handleSubmit(onContinue)} 
                        sx={{
                            maxWidth: 600, 
                            alignSelf: 'center', 
                            marginTop: theme.spacing(2)
                        }}>

                    {step === ChangePasswordStepEnum.Step1 &&
                        <>
                            <Box style={{ display: 'flex', flexDirection: 'row', marginBottom: '16px' }}>
                                <Lock fontSize='large' />
                                <Typography variant='h5' color="textSecondary" style={{ marginTop: 'auto' }}>
                                    {t(newUser ? 
                                        'changePassword.createPasswordMessage' : 
                                        forgotPasswordGuid ? 
                                        'changePassword.passwordExpiredMessage' :
                                        'changePassword.info')}
                                </Typography>
                            </Box>

                            <Typography variant="subtitle1" color='textSecondary'>
                                {t(forgotPasswordGuid ? 'changePassword.promptEmail' : 'changePassword.prompt')}
                            </Typography>

                            <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}>
                                    {forgotPasswordGuid && 
                                        <FormInput
                                            id='email' 
                                            name="email"
                                            label={t("login.emailAddress")}
                                            rules={{
                                                required: true,
                                                validate: (value: any) => _validateEmail(value) ||
                                                    t('changePassword.emailRequired')
                                            }}
                                        />
                                    }
                                    {!forgotPasswordGuid && 
                                        <FormPassword
                                            name="currentpassword"
                                            id="currentpassword"
                                            testId="currentpassword"
                                            label={t("login.password")}
                                        />
                                    }
                            </Box>
                        </>
                    }
                    {
                        step === ChangePasswordStepEnum.NewPassword &&
                        <>
                            <Box style={{ display: 'flex', flexDirection: 'row', marginBottom: '16px' }}>
                                <Lock fontSize='large' />
                                <Typography variant='h5' color="textSecondary" style={{ marginTop: 'auto' }}>
                                    {t('changePassword.createNewPrompt')}
                                </Typography>
                            </Box>

                            <FormPassword
                                name="newpassword"
                                id="newpassword"
                                testId="newpassword"
                                label={t("changePassword.newPassword")}
                                rules={{
                                    required: t('changePassword.confirmPasswordError'),
                                    validate: (value: any) => _validateNewPassword(value) ||
                                        t('changePassword.newPasswordError')
                                }}
                                sx={{marginBottom: theme.spacing(2)}}
                            />

                            <FormPassword
                                name="confirmpassword"
                                id="confirmpassword"
                                testId="confirmpassword"
                                label={t("changePassword.confirmPassword")}
                                rules={{
                                    required: t('changePassword.confirmPasswordError'),
                                    validate: (value: any) => _validateConfirmPassword(value) ||
                                        t('changePassword.confirmPasswordError')
                                }}
                                sx={{marginBottom: theme.spacing(2)}}
                            />

                            <Alert severity="info">
                                <AlertTitle>{t('changePassword.ruleTitle')}</AlertTitle>

                                <Typography variant='body2' component='div'>
                                    {t('changePassword.rule1')}
                                </Typography>
                                <Typography variant='body2' component='div'>
                                    {t('changePassword.rule2')}
                                </Typography>
                                <Typography variant='body2' component='div'>
                                    {t('changePassword.rule3')}
                                </Typography>
                                <Typography variant='body2' component='div'>
                                    {t('changePassword.rule4')}
                                </Typography>
                                <Typography variant='body2' component='div'>
                                    {t('changePassword.rule5')}
                                </Typography>
                            </Alert>
                        </>
                    }

                    <Button
                        disabled={!canContinue}
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="secondary"
                        sx={{margin: theme.spacing(3, 0, 2)}}
                        {...register("continue")}
                    >
                        {
                            step === ChangePasswordStepEnum.Step1 ?
                                t("buttons.continue") :
                                t("changePassword.createNewPassword")
                        }
                    </Button>

                </Form>

            </FullScreenDialog>

            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserConfirmation}
            />
        </>

    );
}
